import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useProvinceContext } from '../../../contexts/ProvinceProvider';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { Province } from '../../../types/province';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import EditProvinceModal from '../../CommonComps/Modals/EditProvinceModal';

const ProvinceModalContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedProvince,
    showDeleteModal,
    showEditModal,
    setShowDeleteModal,
    setShowEditModal,
    setTargetedProvince,
    setSearchText,
  } = useProvinceContext();

  // integration of custom API hooks here
  const { deleteProvinceByIdHandler } = useProvincesActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete province feature declared here
  const deleteProvinceHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteProvinceByIdHandler
        .mutateAsync({ provinceId: targetedProvince?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Province Deleted!!');

            setShowDeleteModal(false);

            setTargetedProvince(null);
            setSearchText('');

            queryClient.invalidateQueries({ queryKey: ['province-list'] });
          } else {
            toast.error(res.data.message || 'Province Deleting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering province modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <EditProvinceModal
            province={targetedProvince as Province}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({targetedProvince?.province_name})
                </span>{' '}
                province? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteProvinceHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProvinceModalContainer;
